import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    saveToStorage,
    clearStorage,
    getFromStorage,
} from '@forvalter/utils/storage';
import type { Aktivitet } from '@klp/formgenerator/interfaces';

enum localStorageReferences {
    SELECTED_LEVERANDOR_STATUS = 'storedSelectedLeverandorStatus',
}

enum leverandorSelectorActionsReferences {
    SAVE_LEVERANDOR_STATUS = 'leverandor/saveselectedLeverandorStatusToStorage',
    CLEAR_LEVERANDOR_STATUS = 'leverandor/clearSelectedLeverandorStatusFromStorage',
}

export type LeverandorActiveModal =
    | { type: 'tildelAnsvarlig'; prosessInstansId: string }
    | {
          type: 'sendKommentar';
          prosessInstansId: string;
          roller: Array<string>;
          defaultRolle?: string;
          activity?: Aktivitet;
      };

export type LeverandorActiveModalType = LeverandorActiveModal['type'];

export type ILeverandorState = {
    activeModal?: LeverandorActiveModal;
    selectedLeverandorStatus: Array<string>;
};

export interface ILeverandorStore {
    leverandor: ReturnType<typeof leverandorSlice.reducer>;
}

const saveSelectedLeverandorStatusToStorage = createAsyncThunk(
    leverandorSelectorActionsReferences.SAVE_LEVERANDOR_STATUS,
    async (status: Array<string>) => {
        if (status) {
            saveToStorage(
                localStorageReferences.SELECTED_LEVERANDOR_STATUS,
                status,
            );
            return status;
        }
        throw new Error('No leverandor status received');
    },
);

const clearSelectedLeverandorStatusFromStorage = createAsyncThunk(
    leverandorSelectorActionsReferences.CLEAR_LEVERANDOR_STATUS,
    async () => {
        clearStorage(localStorageReferences.SELECTED_LEVERANDOR_STATUS);
        return null;
    },
);

const selectedLeverandorStatusFromStorage =
    (getFromStorage(localStorageReferences.SELECTED_LEVERANDOR_STATUS) as
        | Array<string>
        | undefined) ?? [];

const initialState: ILeverandorState = {
    selectedLeverandorStatus: selectedLeverandorStatusFromStorage,
    activeModal: undefined,
};

const leverandorSlice = createSlice({
    name: 'leverandor',
    initialState,
    reducers: {
        setActiveModal: (state, action) => {
            state.activeModal = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(
                saveSelectedLeverandorStatusToStorage.fulfilled,
                (state, action) => {
                    return {
                        ...state,
                        selectedLeverandorStatus: action.payload,
                    };
                },
            )
            .addCase(
                clearSelectedLeverandorStatusFromStorage.fulfilled,
                (state) => {
                    return {
                        ...state,
                        selectedLeverandorStatus: [],
                    };
                },
            );
    },
});

export const leverandorActions = {
    ...leverandorSlice.actions,
    saveSelectedLeverandorStatusToStorage,
    clearSelectedLeverandorStatusFromStorage,
    reducerActions: leverandorSlice.actions,
};

export const leverandorSelectors = ({ leverandor }: ILeverandorStore) =>
    leverandor;

export const isActiveModal = <T extends LeverandorActiveModalType>(
    activeModal: LeverandorActiveModal | undefined,
    type: T,
): activeModal is Extract<LeverandorActiveModal, { type: T }> =>
    activeModal?.type === type;

export { localStorageReferences, leverandorSelectorActionsReferences };

export default leverandorSlice;
